import { useState, useEffect } from "react";
import "../css/UnitList.css";
import * as XLSX from "xlsx";

const PurchaseReport = () => {
  const API_URL = process.env.REACT_APP_API_URL || "";

  const [history, setHistory] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [invoiceFilter, setInvoiceFilter] = useState("");

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(`${API_URL}product-list.php`);
        const data = await response.json();
        if (data.status && Array.isArray(data.data)) {
          setProducts(data.data);
        }
      } catch (error) {
        showToast("Failed to load products", "error");
      }
    };
    fetchProducts();
  }, [API_URL]);

  // Fetch purchase history
  const fetchHistory = async () => {
    setIsLoading(true);
    try {
      const requestBody = {
        ...(selectedProductId && { product_id: selectedProductId }),
        ...(startDate && { start_date: startDate }),
        ...(endDate && { end_date: endDate }),
      };

      const response = await fetch(`${API_URL}add-list-purchases.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });
      const data = await response.json();

      if (data.status && Array.isArray(data.purchases)) {
        setHistory(data.purchases);
      } else if (data.status && Array.isArray(data.data)) {
        setHistory(data.data);
      } else {
        setHistory([]);
        showToast(data.message || "No history found.", "info");
      }
    } catch (error) {
      showToast("Network error. Please try again.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, [selectedProductId, API_URL]);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "" });
    }, 3000);
  };

  // Filter history by date range on the frontend
  function filterByDateRange(data, startDate, endDate) {
    if (!startDate && !endDate) return data;
    return data.filter(item => {
      const itemDate = item.create_date ? item.create_date.slice(0, 10) : '';
      if (startDate && endDate) {
        return itemDate >= startDate && itemDate <= endDate;
      } else if (startDate) {
        return itemDate >= startDate;
      } else if (endDate) {
        return itemDate <= endDate;
      }
      return true;
    });
  }

  // Get unique invoice numbers for the select dropdown
  const uniqueInvoiceNumbers = Array.from(new Set(history.map(item => item.invoice_no))).filter(Boolean);

  const filteredHistory = filterByDateRange(history, startDate, endDate).filter(item =>
    invoiceFilter === "" ||
    (item.invoice_no && item.invoice_no === invoiceFilter)
  );

  // Export to Excel function
  const exportToExcel = () => {
    if (!filteredHistory.length) return;
    const worksheetData = filteredHistory.map(item => ({
      "Invoice No": item.invoice_no,
      "Product Name": item.product_name,
      "Date": new Date(item.create_date).toLocaleString(),
      "Type": item.type,
      "Quantity": `${item.quantity} ${item.unit_name}`,
      "Unit Price": `₹${item.per_unit_price}`,
      "Total Amount": `₹${item.total_amt}`,
    }));
    // Calculate total amount
    const totalAmount = filteredHistory.reduce((sum, item) => sum + parseFloat(item.total_amt || 0), 0);
    worksheetData.push({
      "Invoice No": "",
      "Product Name": "Total",
      "Date": "",
      "Type": "",
      "Quantity": "",
      "Unit Price": "",
      "Total Amount": `₹${totalAmount}`,
    });
    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Purchase Report");
    XLSX.writeFile(workbook, "purchase_report.xlsx");
  };

  return (
    <div className="category-list-content">
      <div className="page-header">
        <div className="header-content">
          <h1>Purchase Report</h1>
          <p>View all purchases</p>
        </div>
      </div>

      <div className="filters-section" style={{
        marginBottom: "40px",
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'flex-start',
        flexWrap: 'nowrap',
        gap: '16px',
        background: '#fff',
        padding: '16px',
        borderRadius: '8px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
        overflowX: 'auto',
        minWidth: 0
      }}>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'nowrap' }}>
          <div className="form-group">
            <label htmlFor="product-filter" style={{ fontSize: '13px' }}>Filter by Product</label>
            <select
              id="product-filter"
              value={selectedProductId}
              onChange={(e) => setSelectedProductId(e.target.value)}
              style={{ height: 45, minWidth: 120, fontSize: '13px' }}
            >
              <option value="">All Products</option>
              {products.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.product}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="invoice-filter" style={{ fontSize: '13px' }}>Filter by Invoice No</label>
            <select
              id="invoice-filter"
              value={invoiceFilter}
              onChange={(e) => setInvoiceFilter(e.target.value)}
              style={{ height: 45, minWidth: 120, fontSize: '13px' }}
            >
              <option value="">All Invoices</option>
              {uniqueInvoiceNumbers.map(inv => (
                <option key={inv} value={inv}>{inv}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="start-date" style={{ fontSize: '13px' }}>Start Date</label>
            <input
              type="date"
              id="start-date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{ height: 45, minWidth: 120, fontSize: '13px' }}
            />
          </div>
          <div className="form-group">
            <label htmlFor="end-date" style={{ fontSize: '13px' }}>End Date</label>
            <input
              type="date"
              id="end-date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{ height: 45, minWidth: 120, fontSize: '13px' }}
            />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'nowrap' }}>
          <button
            className="btn btn-outline-danger"
            onClick={() => {
              setSelectedProductId("");
              setStartDate("");
              setEndDate("");
              setInvoiceFilter("");
            }}
            style={{ height: 45, minWidth: 80, fontWeight: 500, fontSize: '13px' }}
          >
            Reset
          </button>
          <button className="btn btn-primary" onClick={exportToExcel} disabled={!history.length} style={{ height: 45, minWidth: 80, fontWeight: 500, fontSize: '13px' }}>
            Export
          </button>
        </div>
      </div>

      {isLoading ? (
        <p>Loading history...</p>
      ) : (
        <div className="unit-list-table-container">
          <table className="unit-list-table">
            <thead>
              <tr>
                <th>Invoice No</th>
                <th>Product Name</th>
                <th>Date</th>
                <th>Type</th>
                <th>Quantity</th>
                <th>Unit Price</th>
                <th>Total Amount</th>
              </tr>
            </thead>
            <tbody>
              {filteredHistory.length > 0 ? (
                filteredHistory.map((item) => (
                  <tr key={item.id}>
                    <td>{item.invoice_no}</td>
                    <td>{item.product_name}</td>
                    <td>{new Date(item.create_date).toLocaleString()}</td>
                    <td>
                      <span
                        className={`status-badge ${item.type.toLowerCase()}`}
                      >
                        {item.type}
                      </span>
                    </td>
                    <td>
                      {item.quantity} {item.unit_name}
                    </td>
                    <td>₹{item.per_unit_price}</td>
                    <td>₹{item.total_amt}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7">No records found.</td>
                </tr>
              )}
              {/* Total row */}
              {filteredHistory.length > 0 && (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'right' }}><b>Total</b></td>
                  <td style={{ backgroundColor: '#ffe066', fontWeight: 'bold' }}>
                    ₹ {filteredHistory.reduce((sum, item) => sum + parseFloat(item.total_amt || 0), 0)} 
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          <div className="toast-content">
            <span className="toast-message">{toast.message}</span>
            <button
              className="toast-close"
              onClick={() => setToast({ show: false, message: "", type: "" })}
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseReport; 